import React, { useContext, useMemo, useState, useCallback } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { MessageBar, MessageBarType } from '@bingads-webui-react/fabric-control';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { processImageListViewModelToModel } from '@bingads-webui-campaign-react/responsive-ad-editor-utils';
import { withRecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { bulkEditChange } from '@bingads-webui-campaign-legacy/bulk-edit/service/bulk-edit';
import { BulkMMAConvertEditorContainer } from './bulk-mma-convert-editor-container';
import { mapBulkUpsertErrors } from '../utils/errors-util';
import { BulkMMAConvertEditorFooter } from './bulk-mma-convert-editor-footer';
import { BulkMMAConvertProgress } from './bulk-mma-convert-progress';
import { BulkMMAConvertError } from './bulk-mma-convert-error';
import { getJobResults } from './repository';
import { getBulkEditParams, processViewModelDataForSaving } from './data-util';

const BulkMMAConvertEditorInternal = ({
  genericImage,
  viewContext,
  onAdSaved,
  onClose,
  perfMarker,
  oDataConfig,
  permissions,
  odata,
  allPages,
  selectedItems,
  filterService,
  selectedRowCount,
}) => {
  const { scope } = useContext(ScopeContext);
  const { cid, aid } = scope.values || {};
  const i18n = useContext(I18nContext);
  const [data, setData] = useState({});
  const [isDataValid, setIsDataValid] = useState(true);
  const [errors, setErrors] = useState([]);
  const [blocking, setBlocking] = useState(false);
  const imageOData = useMemo(() => new ImageOData(cid, aid, oDataConfig), [cid, aid, oDataConfig]);
  const [saveFailed, setSaveFailed] = useState(false);
  const [isUnSaved, setUnSaved] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [bulkSuccessCount, setBulkSuccessCount] = useState(0);
  const [bulkErrorCount, setBulkErrorCount] = useState(0);
  const [bulkTotalCount, setBulkTotalCount] = useState(0);
  const [bulkInlineErrors, setBulkInlineErrors] = useState([]);
  const [bulkFailed, setBulkFailed] = useState(false);
  const [showBulkPartialError, setShowBulkPartialError] = useState(false);
  const [genericErrorMessages, setGenericErrorMessages] = useState([]);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const onDataChange = useCallback(({ data: newData, isValid }) => {
    setData(newData);
    setIsDataValid(isValid);
  }, []);

  const onProgess = useCallback(() => {
    setShowProgress(true);
  }, []);

  const onHideProgress = useCallback(() => {
    setShowProgress(false);
    onClose();
  }, [onClose]);

  const onSave = useCallback(() => {
    if (isDataValid) {
      setSaveFailed(false);
      setBlocking(true);
      clearAllErrors();
      const adData = processViewModelDataForSaving(data);
      return processImageListViewModelToModel(adData.Images.ImageList, imageOData)
        .then((result) => {
          const {
            mtModel,
          } = result;

          const params = _.defaults(mtModel, adData);
          const bulkParams = getBulkEditParams(params, { allPages, selectedItems, filterService });
          return bulkEditChange({
            params: bulkParams,
            progressCB: onProgess,
            completeCB: (response) => {
              setShowProgress(false);
              setBlocking(false);
              if (response.Status === 'CompletedWithErrors') {
                setShowBulkPartialError(true);
                setBulkSuccessCount(response.SuccessRowCount);
                setBulkTotalCount(response.TotalRowCount);
                setBulkErrorCount(response.ErrorRowCount);
                setUnSaved(true);
                setBulkFailed(true);
                getJobResults(response.Id, 'errors', 0, 100, 'Ad', odata)
                  .done((jobResponse) => {
                    if (jobResponse && jobResponse.value) {
                      const err = jobResponse.value.map((errorObject) => {
                        const errorKey = errorObject.Errors[0];
                        const entityName = errorObject.EntityKey[0].Value;

                        return {
                          error: i18n.getString(`Error_Campaign_API_${errorKey}`),
                          name: entityName,
                        };
                      });

                      setBulkInlineErrors(err);
                    }
                  });
              } else if (response.Status === 'Completed') {
                onAdSaved();
              }
              return response;
            },
            failedCB: (e) => {
              // mma bulk conversion failed
              setUnSaved(true);
              setBulkFailed(true);
              const errorMes = e.errors.map(error => JSON.stringify(error));
              setGenericErrorMessages(errorMes);
            },
          })
            .finally(() => { setBlocking(false); });
        })
        .catch((response) => {
          // mma bulk get params failed
          setSaveFailed(true);
          if (response) {
            const resErrors = _.unique(response.errors, false, err => err.ErrorNumber);
            setErrors(mapBulkUpsertErrors({
              errors: resErrors, i18n, scenario: 'mmaSave',
            }));
            return false;
          }
          return false;
        })
        .finally(() => { setBlocking(false); });
    }
    setSaveFailed(true);
    return Promise.resolve(false);
  }, [allPages, clearAllErrors, data, filterService, i18n, imageOData, isDataValid, odata, onAdSaved, onProgess, selectedItems]);

  return (
    <UiBlocker
      blocking={blocking}
      loadingText={i18n.getString('Loading')}
    >
      <BulkMMAConvertEditorContainer
        data={data}
        errors={errors}
        onDataChange={onDataChange}
        perfMarker={perfMarker}
        permissions={permissions}
        viewContext={viewContext}
        genericImage={genericImage}
        showAllValidationErrors={saveFailed}
        oDataConfig={oDataConfig}
        selectedRowCount={selectedRowCount}
      />
      <BulkMMAConvertProgress
        i18n={i18n}
        onHide={onHideProgress}
        show={showProgress}
        totalCount={bulkTotalCount}
        completeCount={bulkSuccessCount + bulkErrorCount}
      />
      <BulkMMAConvertError
        show={showBulkPartialError}
        errorCount={bulkErrorCount}
        inlineErrors={bulkInlineErrors}
        i18n={i18n}
      />
      <div className="generic-errors error">
        {
          genericErrorMessages.map((errMsg, index) => (
            <div className="error" key={`${index}`}>
              {errMsg}
            </div>
          ))
        }
      </div>
      <BulkMMAConvertEditorFooter
        i18n={i18n}
        showSave={!isUnSaved}
        onSave={onSave}
        onClose={onClose}
      />
      {
        bulkFailed &&
        <MessageBar
          messageBarType={MessageBarType.warning}
          isMultiline={false}
          styles={{ root: { width: '100%' } }}
          onDismiss={() => setBulkFailed(false)}
        >
          {i18n.getString(_TL_('we’re unable to save your multimedia ad(s) due to an error. Please try again. '))}
        </MessageBar>
      }
    </UiBlocker>
  );
};

BulkMMAConvertEditorInternal.propTypes = {
  selectedItems: PropTypes.arrayOf(PropTypes.shape({})),
  allPages: PropTypes.bool,
  filterService: PropTypes.shape({}),
  selectedRowCount: PropTypes.number,
  genericImage: PropTypes.string.isRequired,
  viewContext: PropTypes.shape({
    currentAdGroup: PropTypes.shape({
      Id: PropTypes.number,
    }),
    currentCampaign: PropTypes.shape({
      Id: PropTypes.number,
    }),
  }).isRequired,
  onAdSaved: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }),
  oDataConfig: PropTypes.shape({
    odataRootUrl: PropTypes.string,
    odataPath: PropTypes.string,
    odataToken: PropTypes.string,
  }),
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({}),
  }),
  odata: PropTypes.shape({
    get: PropTypes.func.isRequired,
    post: PropTypes.func.isRequired,
  }).isRequired,
};

BulkMMAConvertEditorInternal.defaultProps = {
  selectedItems: [],
  allPages: false,
  selectedRowCount: 0,
  filterService: {},
  perfMarker: {
    willFetchFile: _.noop,
    willInit: _.noop,
    willFetchData: _.noop,
    willRender: _.noop,
    done: _.noop,
    createChild: _.noop,
  },
  oDataConfig: null,
  permissions: { dynamic: {} },
};

export const BulkMMAConvertEditor = _.compose(withRecommendationContext)(BulkMMAConvertEditorInternal);
